import type { ViewerShell } from '../../../shell';
import { openDialog, paragraph } from '../../../ui/dialogs';
import { el } from './dom';

/**
 * A short text asked in an in-viewer dialog (never window.prompt): the question about a region, the
 * name of a tracked lesion. Suggestions fill the field with one click. Resolves null on Cancelar,
 * Esc or a click on the backdrop.
 */

export interface AskTextOptions {
  title: string;
  label: string;
  hint: string;
  className: string;
  confirm: string;
  initial: string;
  suggestions?: readonly string[];
  minLength: number;
  maxLength: number;
  tooShort: string;
}

export const ASK_CANCEL = 'Cancelar';

function suggestionRow(suggestions: readonly string[], field: HTMLTextAreaElement): HTMLElement {
  const row = el('div', 'assistant-ask-suggestions');
  row.setAttribute('role', 'group');
  for (const text of suggestions) {
    const b = el('button', 'assistant-chip assistant-ask-suggestion', text);
    b.type = 'button';
    b.addEventListener('click', () => {
      field.value = text;
      field.focus();
    });
    row.appendChild(b);
  }
  return row;
}

function buildField(options: AskTextOptions): HTMLTextAreaElement {
  const field = el('textarea', 'assistant-ask-input');
  field.id = `${options.className}-input`;
  field.rows = 3;
  field.maxLength = options.maxLength;
  field.value = options.initial;
  return field;
}

export function askText(shell: ViewerShell, options: AskTextOptions): Promise<string | null> {
  return new Promise((resolve) => {
    let answered = false;
    const field = buildField(options);
    const error = el('p', 'assistant-ask-error');
    error.setAttribute('role', 'alert');
    const finish = (value: string | null, close: () => void) => {
      answered = true;
      close();
      resolve(value);
    };
    const submit = (close: () => void) => {
      const value = field.value.trim();
      if (value.length < options.minLength) {
        error.textContent = options.tooShort;
        field.focus();
        return;
      }
      finish(value.slice(0, options.maxLength), close);
    };
    const { body, dialog } = openDialog(shell, {
      title: options.title,
      className: `assistant-ask-dialog ${options.className}`,
      buttons: [
        { label: ASK_CANCEL, onClick: (close) => finish(null, close) },
        { label: options.confirm, primary: true, onClick: submit },
      ],
    });
    const label = el('label', 'assistant-ask-label', options.label);
    label.htmlFor = field.id;
    paragraph(body, options.hint, 'assistant-ask-hint');
    body.appendChild(label);
    if (options.suggestions?.length) body.appendChild(suggestionRow(options.suggestions, field));
    body.append(field, error);
    field.focus();
    field.select();
    const observer = new MutationObserver(() => {
      if (dialog.isConnected) return;
      observer.disconnect();
      if (!answered) resolve(null);
    });
    observer.observe(shell.regions.overlayLayer, { childList: true });
  });
}
